
import { defineComponent, ref, reactive, computed } from 'vue'
import { FormDataStructure, FieldStructure } from '@/contracts/forms/FieldContracts'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { getStage1FormDataStructure, getStage2FormDataStructure, getAdditionalFormDataStructure } from '@/composables/formdata'
import { search } from '@/composables/search'
import { nuxeoBlobPath } from '@/composables/api'
import { pagination } from '@/composables/pagination'
import { prepareTableColumns, getFormattedColumnValue } from '@/composables/table'
import pdf from 'pdfvuer'
import Form from '@/components/Form.vue'
import { prepareSearchDates } from '@/composables/pageprovider'
import { BasketItem } from '@/contracts/store/BasketContracts'

export default defineComponent(
{
    name: 'Results',
    components:
    {
        pdf,
        Form
    },
    setup()
    {
        // Load router:
        const router = useRouter()
        // Use vuex:
        const store = useStore()
        // Get form data:
        const stage1Data = store.getters['formData/stage1Data']
        const stage2Data = store.getters['formData/stage2Data']
        const stageData = reactive(Object.assign(stage1Data, stage2Data))
        // Get form structure:
        const stage1Fields = getStage1FormDataStructure()
        const stage2Fields = getStage2FormDataStructure(stageData.grantType)
        // Set columns to display based on grant type:
        const columns = prepareTableColumns(stageData.grantType)
        // Get basket from store:
        const basketItems = store.getters['basket/getBasket']
        // Setup search:
        const {
            data,
            numberOfPaginationPages,
            currentPageIndex,
            sortDirection,
            sortColumn,
            runSearch,
            prevPage,
            nextPage,
            goToPage
        } = search(stageData, 1)
        // Setup pagination:
        const {
            currentPageNumber,
            numberOfResults,
            numberOfResultsText,
            isFirstPage,
            isLastPage,
            paginationPages
        } = pagination(currentPageIndex, numberOfPaginationPages, data, 1)
        // Setup cal search:
        const {
            data: dataCal,
            numberOfPaginationPages: numberOfPaginationPagesCal,
            currentPageIndex: currentPageIndexCal,
            sortDirection: sortDirectionCal,
            sortColumn: sortColumnCal,
            runSearch: runSearchCal,
            prevPage: prevPageCal,
            nextPage: nextPageCal,
            goToPage: goToPageCal
        } = search(stageData, 2)
        // Setup cal pagination:
        const {
            currentPageNumber: currentPageNumberCal,
            numberOfResults: numberOfResultsCal,
            numberOfResultsText: numberOfResultsTextCal,
            isFirstPage: isFirstPageCal,
            isLastPage: isLastPageCal,
            paginationPages: paginationPagesCal
        } = pagination(currentPageIndexCal, numberOfPaginationPagesCal, dataCal, 2)

        // Handle ordering of columns:
        const sortStatusMessage = ref('')
        const sortBy = function(column: string, direction: string, friendlyName: string)
        {
            // Set new sort column:
            sortColumn.value = column
            // Set sort direction:
            if(sortDirection.value === 'none' || sortDirection.value === 'descending')
            {
                sortDirection.value = 'ascending'
            }
            else
            {
                sortDirection.value = 'descending'
            }
            // Set status message for screen readers:
            sortStatusMessage.value = 'Sort by ' + friendlyName + ' (' + sortDirection.value + ')'
            // Trigger search:
            runSearch()
        }
        // Handle changing of years:
        const prevYear = function(field: FieldStructure)
        {
            if(stageData[field.name] !== undefined)
            {
                stageData[field.name]--
            }
            else if(stageData[field.name] !== undefined)
            {
                stageData[field.name]--
            }
        }
        const nextYear = function(field: FieldStructure)
        {
            if(stageData[field.name] !== undefined)
            {
                stageData[field.name]++
            }
            else if(stageData[field.name] !== undefined)
            {
                stageData[field.name]++
            }
        }

        // Handle checking data type for search fields:
        const showSummaryField = function(field: FieldStructure, data: FormDataStructure): boolean
        {
            if(data[field.name] === undefined)
            {
                return false
            }
            if(typeof data[field.name] === 'string' && data[field.name] === '')
            {
                return false
            }
            return true
        }

        // Format search field value appearance:
        const getFormattedSummaryValue = function(field: FieldStructure, data: FormDataStructure): string | number
        {
            if(field.type === 'Radio')
            {
                if(data[field.name] === 1)
                {
                    return 'Yes'
                }
                return 'No'
            }
            return data[field.name]
        }

        // Determine if field is a year:
        const isYearField = function(field: FieldStructure): boolean
        {
            if(field.rules.year !== undefined)
            {
                return true
            }
            return false
        }

        // Handle checking if in basket:
        const isInBasket = function(uid: string): boolean
        {
            for(let i = 0; i < basketItems.length; i++)
            {
                if(basketItems[i].uid === uid)
                {
                    return true
                }
            }
            return false
        }
        // Handle adding to basket:
        const prepareForBasket = function(index: number)
        {
            const entry = data.entries[index]
            const basketItem =
            {
                uid: entry.uid,
                'hmctsgrant:firstnames': entry.properties['hmctsgrant:firstnames'],
                'hmctsgrant:surname': entry.properties['hmctsgrant:surname'],
                'hmctsgrant:dateofprobate': entry.properties['hmctsgrant:dateofprobate'],
                'hmctsgrant:probatenumber': entry.properties['hmctsgrant:probatenumber'],
                'hmctsgrant:dateofdeath': entry.properties['hmctsgrant:dateofdeath'],
                'hmctsgrant:grantdocTypeoOfName': entry.properties['hmctsgrant:grantdocTypeoOfName'],
                'hmctsgrant:grantdocTypeOf': entry.properties['hmctsgrant:grantdocTypeOf'],
                'hmctsgrant:registryofficename': entry.properties['hmctsgrant:registryofficename'],
                'hmctsgrant:regimentnumber': entry.properties['hmctsgrant:regimentnumber'],
                'hmctsgrant:price': entry.properties['hmctsgrant:price']
            }
            addToBasket(basketItem)
        }
        // Handle removing to basket:
        const removeFromBasket = function(index: number)
        {
            store.commit('basket/removeFromBasket', index)
        }
        // Handle adding to basket:
        const addToBasket = function(basketItem: BasketItem)
        {
            store.commit('basket/addToBasket', basketItem)
        }

        // PDF vars:
        const windowWidth = window.innerWidth
        const isMobile = ref(true)
        const pdfScale = ref(1)
        const pdfResizeNormal = true
        const pdfResizeLarge = false
        const pdfFullscreen = ref(false)
        const pdfShowForm = ref(false)
        const pdfShowFormText = ref('Order documents')
        const pdfFormattedZoom = computed(() =>
        {
            return Math.round(Number(pdfScale.value * 100))
        })
        // Set for mobile:
        if(windowWidth >= 576)
        {
            isMobile.value = false
            pdfShowForm.value = true
        }
        // Handle viewing image:
        const viewImage = function()
        {
            pdfFullscreen.value = !pdfFullscreen.value
            if(pdfFullscreen.value)
            {
                document.body.style.position = 'fixed'
                document.body.style.top = `-${window.scrollY}px`
            }
            else
            {
                document.body.style.position = ''
                document.body.style.top = ''
            }
        }
        const toggleForm = function()
        {
            pdfShowForm.value = !pdfShowForm.value
            if(pdfShowForm.value)
            {
                pdfShowFormText.value = 'Hide form'
            }
            else
            {
                pdfShowFormText.value = 'Order documents'
            }
        }
        const getImageUrl = computed(() =>
        {
            if(dataCal.entries[0].properties['file:content'] !== undefined)
            {
                const url = dataCal.entries[0].properties['file:content'].data
                return process.env.VUE_APP_API_DOMAIN + nuxeoBlobPath(url)
            }
            return ''
        })

        // Set fieldsets:
        const fieldsets =
        [
            {
                name: 'lastname',
                text: 'What was their last name?',
                size: 'govuk-fieldset__legend--m'
            },
            {
                name: 'firstname',
                text: 'What was their first name?',
                size: 'govuk-fieldset__legend--m'
            },
            {
                name: 'dateofprobate',
                text: 'What was their date of probate?',
                size: 'govuk-fieldset__legend--m',
                hide: true
            },
            {
                name: 'dateofdeath',
                text: 'What was their date of death?',
                size: 'govuk-fieldset__legend--m',
                hide: true
            },
            {
                name: 'registrylocation',
                text: 'What is the registry location?',
                size: 'govuk-fieldset__legend--m'
            },
            {
                name: 'folionumber',
                text: 'What is the folio number?',
                size: 'govuk-fieldset__legend--m'
            }
        ]
        // Set fields:
        const fields = getAdditionalFormDataStructure(stage1Data.yearofdeath)
        // Set buttons:
        const buttons =
        {
            submit:
            {
                name: 'submit',
                type: 'Submit',
                text: 'Add to basket'
            }
        }
        // Handle submission:
        const saveFormData = (formData: FormDataStructure) =>
        {
            const dateofprobateArray = formData.dateofprobate.split('/')
            const dateofprobate = prepareSearchDates(dateofprobateArray[2], dateofprobateArray[1], dateofprobateArray[0])
            const dateofdeathArray = formData.dateofdeath.split('/')
            const dateofdeath = prepareSearchDates(dateofdeathArray[2], dateofdeathArray[1], dateofdeathArray[0])
            const entry = dataCal.entries[0]
            const basketItem =
            {
                caldoc_uid: entry.uid,
                'hmctsgrant:firstnames': formData.firstname,
                'hmctsgrant:surname': formData.lastname,
                'hmctsgrant:dateofprobate': dateofprobate.searchDateFrom,
                'hmctsgrant:dateofdeath': dateofdeath.searchDateFrom,
                'hmctsgrant:grantdocTypeOf': 2,
                'hmctsgrant:registryofficename': formData.registrylocation,
                'hmctsgrant:folionumber': formData.folionumber
            }
            addToBasket(basketItem)
            // Hide pdf:
            pdfFullscreen.value = false
            document.body.style.position = ''
            document.body.style.top = ''
            // Set for mobile:
            if(windowWidth < 576)
            {
                pdfShowForm.value = false
            }
            // Show success:
            store.commit('confirmations/setConfirmationData',
            {
                title: 'Added to basket',
                body: 'Your entry has been added to your basket.'
            })
            router.push({ hash: '#confirmation-summary-container' })
        }

        // Return data:
        return {
            stage1Fields,
            stage2Fields,
            stageData,
            columns,
            basketItems,
            data,
            dataCal,
            currentPageNumber,
            currentPageNumberCal,
            numberOfResults,
            numberOfResultsCal,
            numberOfResultsText,
            numberOfResultsTextCal,
            isFirstPage,
            isFirstPageCal,
            isLastPage,
            isLastPageCal,
            paginationPages,
            paginationPagesCal,
            prevPage,
            prevPageCal,
            nextPage,
            nextPageCal,
            goToPage,
            goToPageCal,
            sortDirection,
            sortColumn,
            sortStatusMessage,
            sortBy,
            prevYear,
            nextYear,
            showSummaryField,
            getFormattedSummaryValue,
            isYearField,
            getFormattedColumnValue,
            isInBasket,
            prepareForBasket,
            addToBasket,
            removeFromBasket,
            pdfScale,
            pdfFormattedZoom,
            pdfResizeNormal,
            pdfResizeLarge,
            pdfFullscreen,
            isMobile,
            pdfShowForm,
            pdfShowFormText,
            viewImage,
            toggleForm,
            getImageUrl,
            fieldsets,
            fields,
            buttons,
            saveFormData
        }
    }
})
